import { useState, useEffect } from 'react'
import { message } from 'antd'

const useAssessments = () => {
  const [loading, setLoading] = useState(false)
  const [assessments, setAssessments] = useState([])

  useEffect(() => {
    const fetchAssessments = async () => {
      setLoading(true)
      try {
        // Simulate API call to fetch assessments from the server/backend
        await new Promise((resolve) => setTimeout(resolve, 800))

        // Normally we would fetch from an API
        // For now, we'll use the assessments from the local mock data
        let response = []
        try {
          const module = await import('../utils/assessments.json')
          if (module && module.default) {
            response = module.default
          } else {
            console.error('Error loading assessments.json: Module or default export not found')
            message.error('Failed to load assessments data')
          }
        } catch (importError) {
          console.error('Error importing assessments.json:', importError)
          message.error('Failed to load assessments data')
        }

        setAssessments(response)
      } catch (error) {
        console.error('Error fetching functional assessments:', error)
        message.error('Failed to load assessments')
      } finally {
        setLoading(false)
      }
    }

    fetchAssessments()
  }, [])

  const addAssessment = async (assessmentData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const newAssessment = {
        id: `assessment-${Date.now()}`,
        ...assessmentData,
        completed: false,
        result: null
      }

      setAssessments([...assessments, newAssessment])
      return newAssessment
    } catch (error) {
      console.error('Error adding assessment:', error)
      message.error('Failed to add assessment')
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateAssessment = async (assessmentId, assessmentData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const updatedAssessments = assessments.map((assessment) => {
        if (assessment.id === assessmentId) {
          return { ...assessment, ...assessmentData }
        }
        return assessment
      })

      setAssessments(updatedAssessments)
      return true
    } catch (error) {
      console.error('Error updating assessment:', error)
      message.error('Failed to update assessment')
      return false
    } finally {
      setLoading(false)
    }
  }

  const deleteAssessment = async (assessmentId) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      const updatedAssessments = assessments.filter((assessment) => assessment.id !== assessmentId)
      setAssessments(updatedAssessments)
      return true
    } catch (error) {
      console.error('Error deleting assessment:', error)
      message.error('Failed to delete assessment')
      return false
    } finally {
      setLoading(false)
    }
  }

  const recordAssessmentResult = async (assessmentId, result, notes) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const updatedAssessments = assessments.map((assessment) => {
        if (assessment.id === assessmentId) {
          return {
            ...assessment,
            result,
            notes: notes || assessment.notes,
            completed: true,
            completedDate: new Date().toISOString()
          }
        }
        return assessment
      })

      setAssessments(updatedAssessments)
      return true
    } catch (error) {
      console.error('Error recording assessment result:', error)
      message.error('Failed to record assessment result')
      return false
    } finally {
      setLoading(false)
    }
  }

  const getAssessmentsByCategory = (category) => {
    return assessments.filter((assessment) => assessment.category === category)
  }

  return {
    loading,
    assessments,
    addAssessment,
    updateAssessment,
    deleteAssessment,
    recordAssessmentResult,
    getAssessmentsByCategory
  }
}

export default useAssessments
